/**
 * Axios API Client with MSAL Authentication
 */
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { IPublicClientApplication } from '@azure/msal-browser';
import { tokenRequest } from '../auth/authConfig';

let msalInstance: IPublicClientApplication | null = null;

export const initializeApiClient = (instance: IPublicClientApplication) => {
  msalInstance = instance;
};

const apiClient: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor to add auth token — never send API requests without a token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (!msalInstance) {
      console.error('API client: MSAL not initialized');
      return Promise.reject(new Error('Authentication not ready. Please sign in again.'));
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      console.error('API client: No signed-in account');
      return Promise.reject(new Error('Please sign in to continue.'));
    }

    const account = accounts[0];
    let token: string | null = null;

    try {
      const response = await msalInstance.acquireTokenSilent({
        ...tokenRequest,
        account,
      });
      token = response.accessToken;
    } catch (silentError: any) {
      console.warn('Silent token acquisition failed, trying popup:', silentError?.message || silentError);
      try {
        const response = await msalInstance.acquireTokenPopup({
          ...tokenRequest,
          account,
        });
        token = response.accessToken;
      } catch (popupError: any) {
        console.error('Token acquisition failed:', popupError);
        return Promise.reject(
          popupError?.message
            ? new Error(popupError.message)
            : new Error('Could not get an access token. Please sign in again.')
        );
      }
    }

    if (!token) {
      return Promise.reject(new Error('No access token. Please sign in again.'));
    }

    config.headers.Authorization = `Bearer ${token}`;
    config.headers['X-Bearer-Token'] = token;

    // SWA proxy strips headers unpredictably — also pass token as query param (backend checks all three)
    const sep = (config.url || '').includes('?') ? '&' : '?';
    config.url = `${config.url || ''}${sep}_token=${encodeURIComponent(token)}`;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('Unauthorized - redirecting to login');
      // Handle 401 errors (token expired, etc.)
    }
    return Promise.reject(error);
  }
);

export default apiClient;
